import { useMemo } from "react";
import { t } from "@lingui/macro";
import { ImperialMetricDisplayPreference } from "@WahooFitness/cloud-client-types";
import { useUserContext } from "@WahooFitness/wahoo-offline-mfe";
import ACGraph from "./Graphs/ACGraph";
import MAPGraph from "./Graphs/MAPGraph";
import TPGraph from "./Graphs/TPGraph";
import { EducationStepContent } from "../types";
import { ThreeDPLogoSvg } from "./ThreeDPLogoSvg";
import GAPGraph from "./Graphs/GAPGraph";
import PotentialArrow from "../fourDP/Graphs/PotentialArrow";

export function useThreeDPSteps(activeStep: number, isStandAlone: boolean): EducationStepContent[] {
  const { currentUser } = useUserContext();
  const isMetric =
    (currentUser?.display_preference?.speed_distance ?? ImperialMetricDisplayPreference.metric) ===
    ImperialMetricDisplayPreference.metric;

  const DTL = "Dimensional Training Load (DTL)";
  const FITNESS_SCORE = "Fitness Score";
  const TC = "Training Capacity";
  const GAP = "Grade Adjusted Pace (GAP)";
  const AC = "Anaerobic Capacity (AC)";
  const AC_ = "Anaerobic Capacity";
  const MAP = "Maximum Aerobic Pace (MAP)";
  const MAP_ = "Maximum Aerobic Pace";
  const MAP_only = "MAP";
  const TP = "Threshold Pace (TP)";
  const TP_ = "Threshold Pace";
  return useMemo(
    () => [
      {
        stepId: 0,
        stepName: "intro",
        headline: t`Intro to the core metrics of a Running Athlete Profile.`,
        component: <ThreeDPLogoSvg activeStep={activeStep} stepId={0} key={0} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: t`Next`,
        headerTitle: isStandAlone ? t`Metrics explained` : t`Start profiling`,
        description: t`Your Running Athlete Profile measures 3 metrics to determine your strengths and weaknesses as a runner. It is used to calibrate workouts and give you the most accurate ${DTL}, ${FITNESS_SCORE} and ${TC}.`,
        ticks: [],
      },
      {
        stepId: 1,
        stepName: "gap",
        headline: GAP,
        component: <GAPGraph activeStep={activeStep} stepId={1} isMetric={isMetric} key={1} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: t`Next`,
        power: "",
        headerTitle: t`Metrics explained`,
        description: isMetric
          ? t`Your Running Athlete Profile is calculated using ${GAP}, which normalizes effort across different gradients. For example, a 6:00/km pace on a steep climb is different from the same pace on flat ground.`
          : t`Your Running Athlete Profile is calculated using ${GAP}, which normalizes effort across different gradients. For example, a 9:00/mile pace on a steep climb is different from the same pace on flat ground.`,
        ticks: [],
      },
      {
        stepId: 2,
        stepName: "ac",
        headline: AC,
        component: <ACGraph activeStep={activeStep} stepId={2} key={2} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: t`Next`,
        headerTitle: t`Metrics explained`,
        description: t`${AC_} is the maximal pace achievable while fatigued, typically measured by a 1-minute maximal effort under fatigue. It distinguishes runners with a finishing kick from those who merely maintain pace or fade.`,
        power: t`1-Min REPEATABLE PACE`,
        ticks: [
          {
            label: t`Time`,
            height: 20,
            labelVariant: "ui-sm",
          },
          {
            height: 20,
            label: t`1 min`,
            labelVariant: "ui-sm-bold",
          },
          {
            height: 20,
            label: t`2 min`,
            labelVariant: "ui-sm-bold",
          },
          {
            height: 20,
            label: t`3 min`,
            labelVariant: "ui-sm-bold",
          },
        ],
      },
      {
        stepId: 3,
        stepName: "map",
        headline: MAP,
        component: <MAPGraph activeStep={activeStep} stepId={3} key={3} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: t`Next`,
        headerTitle: t`Metrics explained`,
        description: t`${MAP_} is the speed you could sustain for roughly 5 minutes at absolute maximum effort — think of this as close to an all out 1500m or mile. Improving your ${MAP_only} raises the ceiling of your ${TP}.`,
        power: t`5-min FRESH PACE`,
        ticks: [
          {
            label: t`Time`,
            height: 20,
            labelVariant: "ui-sm",
          },
          ...Array.from({ length: 4 }, () => ({
            height: 9,
          })),
          {
            label: t`5 min`,
            height: 20,
            labelVariant: "ui-sm-bold",
          },
        ],
      },
      {
        stepId: 4,
        stepName: "tp",
        headline: TP,
        component: <TPGraph activeStep={activeStep} stepId={4} key={4} />,
        skippable: false,
        onContinue: () => {},
        disabled: false,
        buttonName: isStandAlone ? t`Close` : t`Start questionnaire`,
        headerTitle: t`Metrics explained`,
        description: t`Your ${TP_} is the pace you could hold for roughly 1 hour. Threshold pace is the cornerstone for all your endurance focused training. This should be the reference for your recovery and long-run targets.`,
        power: t`60-min Pace`,
        endAdornment: <PotentialArrow variant="threeDP" />,
        ticks: [
          {
            label: t`Time`,
            height: 20,
            labelVariant: "ui-sm",
          },
          ...Array.from({ length: 11 }, () => ({
            height: 9,
          })),
          {
            label: t`60 min`,
            height: 20,
            labelVariant: "ui-sm-bold",
          },
        ],
      },
    ],
    [activeStep, isStandAlone, isMetric]
  );
}
